import { useState } from "react";
import { useGroupKey } from "../../lib/useGroupKey.js";
import { sealGroupKeyFor, proveDeviceKey } from "../../lib/keyring.js";
import { trpcClient, errorMessage } from "../../lib/trpc.js";
import { useAuth } from "../../lib/auth.js";
import { useT } from "../../lib/i18n.js";
import { openFilePicker } from "../../lib/filePicker.js";
import { downloadRecoveryFile, helpRequestSchema, readSmallJson } from "../../lib/recovery.js";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import "../account/RecoverySection.css";

export function HelpRecovery({ groupId }: { groupId: string }) {
  const t = useT();
  const { user } = useAuth();
  const state = useGroupKey(groupId);
  const [open, setOpen] = useState(false);
  const [ticket, setTicket] = useState("");
  const [review, setReview] = useState<Awaited<ReturnType<typeof trpcClient.recovery.review.mutate>> | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [helperProof, setHelperProof] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  async function work(action: () => Promise<void>) {
    setBusy(true); setError(null);
    try { await action(); } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  return <>
    <Button variant="secondary" onClick={() => { setOpen(true); setDone(false); setReview(null); setConfirmed(false); setTotpCode(""); setError(null); }}>{t("Help a member recover access")}</Button>
    <Dialog open={open} onClose={() => setOpen(false)} title={t("Help a member recover access")}>
      <div className="fc-recovery__form">
        <p>{t("Only help someone you already know. Speak to them in person or on a call and compare the fingerprint on their recovery screen. A matching email address or a message alone is not enough.")}</p>
        {state !== "ready" && <p role="status">{t("Open this group's photos on this device before approving recovery.")}</p>}
        <Button variant="secondary" disabled={busy} onClick={() => openFilePicker({ accept: ".json", onPick: (files) => { if (files[0]) void work(async () => {
          const request = helpRequestSchema.parse(await readSmallJson(files[0]!));
          const result = await trpcClient.recovery.review.mutate({ groupId, ticket: request.ticket });
          if (result.fingerprint !== request.fingerprint) throw new Error(t("The fingerprints do not match. Ask for a new request."));
          setHelperProof(await proveDeviceKey(result.helperChallenge));
          setTicket(request.ticket); setReview(result); setConfirmed(false); setDone(false);
        }); } })}>{t("Choose their request file")}</Button>
        {review && !done && <>
          <p><strong>{review.displayName}</strong> ({review.username})</p>
          <p>{review.accountReset ? t("Approving allows this person to reset their account password and reopen this group's photos. Their other groups need separate help. Two-factor authentication stays enabled.") : t("Approving restores this group's photos to their current account key. Their password and group role stay unchanged.")}</p>
          <code className="fc-recovery__fingerprint">{review.fingerprint}</code>
          <label><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> {t("I spoke to this person and the fingerprints match.")}</label>
          {user?.totpEnabled && <Field label={t("Authenticator code")} required><Input value={totpCode} inputMode="numeric" maxLength={6} autoComplete="one-time-code" onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} /></Field>}
          <Button variant="primary" disabled={!confirmed || state !== "ready"} loading={busy} onClick={() => void work(async () => {
            const wrappedGroupKey = await sealGroupKeyFor(groupId, review.publicKey);
            const result = await trpcClient.recovery.approve.mutate({ groupId, ticket, helperTicket: review.helperTicket, helperProof, wrappedGroupKey, confirmedFingerprint: review.fingerprint, totpCode: totpCode || undefined });
            downloadRecoveryFile("myfriends-recovery-approval.json", { format: "myfriends-approval-v1", approval: result.approval });
            setDone(true); setTotpCode("");
          })}>{t("Approve and download reply")}</Button>
        </>}
        {done && <><p role="status">{t("Send the approval file back to your friend. They must choose it on the recovery page before the request expires.")}</p>
          {review && !review.accountReset && <p>{t("Share more groups with this person? Open each group's settings and approve the same request file there. Each approval restores only that group.")}</p>}</>}
        {error && <p role="alert" className="fc-auth__error">{error}</p>}
      </div>
    </Dialog>
  </>;
}
